import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { useCallback, useEffect, useRef } from 'react';

export interface FetcherError extends Error {
    info?: unknown;
    status?: number;
}

export function useSSOFetchCloud() {
    const cloakRef = useRef(useAuthStore.getState().keyCloak);

    useEffect(() => useAuthStore.subscribe((state) => (cloakRef.current = state.keyCloak)), []);

    const fetchCloud = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async <T = any>(
            endpoint: string,
            method: string,
            body?: Record<string, unknown>,
            fileUpload = false,
            imageUpload = false,
            domain?: string, // For APIs that are not on the same domain as the app
        ) => {
            if (!endpoint || !method) {
                throw new Error('Missing params');
            }

            await cloakRef.current?.updateToken(5);
            if (!cloakRef.current?.token) {
                throw new Error('Unauthorized');
            }

            const contentType = 'application/json';
            let fetchBody: string | FormData | undefined = undefined;

            if (body) {
                if (fileUpload) {
                    fetchBody = new FormData();
                    for (const key in body) {
                        fetchBody.append(key, body[key] as Blob);
                    }
                } else {
                    fetchBody = JSON.stringify(body);
                }
            }
            // If domain is provided, use it; otherwise, use the default API URL
            const url = domain
                ? `${domain}/api/`
                : imageUpload
                  ? `${process.env.EXPO_PUBLIC_IMAGE_UPLOAD_HOST!}/api/`
                  : '/api/';

            const res = await fetch(`${url}${endpoint}`, {
                body: fetchBody,
                headers: fileUpload
                    ? new Headers({ Authorization: 'Bearer ' + cloakRef.current.token })
                    : new Headers({
                          Authorization: 'Bearer ' + cloakRef.current.token,
                          'Content-Type': contentType,
                      }),
                method: method.toUpperCase(),
            });
            const text = await res.text();
            const data = text ? (JSON.parse(text) as unknown) : {};

            if (res.status === 400) {
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw { ...(data as Record<string, unknown>), status: res.status };
            }

            if (!res.ok) {
                throw data;
            }

            return data as T;
        },
        [],
    );

    return { fetchCloud };
}
